// Step 5d Phase 5 — offline cache inventory + "take offline" picker.
//
// Read-only truth from the encrypted `scopes` store (listScopes) — the screen a pilot checks
// before departing: per-scope item count + last-refreshed, age colour-coded against the §8
// thresholds, with per-scope and global "refresh now". Plus a manual picker to PIN extra date
// ranges (cached + persisted in the encrypted pinned list, kept current by the rolling
// refresh) and RELEASE them (evicts their exclusively-owned rows). Tablet-portrait weighted.

import { useCallback, useEffect, useState } from 'react';
import {
  listScopes, getPinnedScopes, pinScope, releasePinnedScope, bookingsScopeKey,
  type ScopeInventoryEntry, type PinnedScope,
} from '../../lib/offline/cache';
import { cacheBookingsWindow, rollingPrefetch, type PrefetchWindow } from '../../lib/offline/prefetch';
import { rangeWindow } from '../../lib/offline/windowMath';
import { ageBucket, BUCKET_DISPLAY, formatAgeShort } from '../../lib/offline/staleness';
import { C, F, todayInTz, addDays } from './theme';
import { ModalOverlay } from './atoms';
import type { Base } from '../../lib/opsBoardTypes';

interface Props {
  bases: Base[];
  /** Scope key of the auto rolling home window (to label it), or null when no home base. */
  homeWindowKey: string | null;
  homeWindow: PrefetchWindow | null;
  anchorTz: string;
  onClose: () => void;
}

function fmtMB(n?: number): string { return n == null ? '?' : (n / (1024 * 1024)).toFixed(2); }
function dateRangeLabel(fromIso?: string, toIso?: string): string {
  if (!fromIso || !toIso) return '—';
  return `${fromIso.slice(0, 10)} → ${toIso.slice(0, 10)}`;
}

export function OfflineCachePanel({ bases, homeWindowKey, homeWindow, anchorTz, onClose }: Props) {
  const [scopes, setScopes] = useState<ScopeInventoryEntry[]>([]);
  const [pins, setPins] = useState<PinnedScope[]>([]);
  const [estimate, setEstimate] = useState<{ usage?: number; quota?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickFrom, setPickFrom] = useState<string>(() => todayInTz(anchorTz));
  const [pickTo, setPickTo] = useState<string>(() => addDays(todayInTz(anchorTz), 7));
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [s, p] = await Promise.all([listScopes(), getPinnedScopes()]);
    setScopes(s);
    setPins(p);
    try { if (navigator.storage?.estimate) setEstimate(await navigator.storage.estimate()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const pinLabelFor = (key: string): string | undefined => pins.find((p) => p.key === key)?.label;

  const labelFor = (e: ScopeInventoryEntry): string => {
    if (e.kind === 'reference') return `Reference: ${e.collection ?? '—'}`;
    if (homeWindowKey && e.key === homeWindowKey) return 'Auto — home base (−2/+7 days)';
    const pinned = pinLabelFor(e.key);
    if (pinned) return `Pinned — ${pinned}`;
    return `Viewed — ${dateRangeLabel(e.from, e.to)}`;
  };

  const onPin = async () => {
    setBusy(true); setMsg(null);
    try {
      const win = rangeWindow(bases, pickFrom, pickTo);
      if (!win) { setMsg('Pick a valid date range.'); return; }
      const ok = await cacheBookingsWindow(win.from, win.to);
      if (!ok) { setMsg('Could not cache that range (offline or locked?).'); return; }
      await pinScope({ key: bookingsScopeKey(null, win.from, win.to), label: `${pickFrom} → ${pickTo}`, from: win.from, to: win.to, pinned_at: new Date().toISOString() });
      setMsg(`Pinned ${pickFrom} → ${pickTo} for offline use.`);
      await refresh();
    } finally { setBusy(false); }
  };

  const onRelease = async (key: string) => {
    setBusy(true);
    try { await releasePinnedScope(key); await refresh(); } finally { setBusy(false); }
  };

  const onRefreshScope = async (e: ScopeInventoryEntry) => {
    setBusy(true);
    try {
      if (e.kind === 'bookings' && e.from && e.to) await cacheBookingsWindow(e.from, e.to);
      else await rollingPrefetch({ homeWindow, force: true }); // reference: refresh the whole set
      await refresh();
    } finally { setBusy(false); }
  };

  const onRefreshAll = async () => {
    setBusy(true);
    try { await rollingPrefetch({ homeWindow, force: true }); await refresh(); } finally { setBusy(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, width: '100%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto', fontFamily: F }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Offline cache</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 18 }}>×</button>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
          What's available offline on this device. {estimate && `Using ${fmtMB(estimate.usage)} MB of ${fmtMB(estimate.quota)} MB.`}
        </div>

        {/* Inventory */}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: C.textMuted }}>CACHED SCOPES</div>
          <button disabled={busy} onClick={onRefreshAll} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>Refresh all</button>
        </div>
        {scopes.length === 0 && <div style={{ fontSize: 12, color: C.textMuted, padding: '10px 0' }}>Nothing cached yet (or device locked).</div>}
        {scopes.map((e) => {
          const ageMs = Date.now() - new Date(e.refreshed_at).getTime();
          const bucket = BUCKET_DISPLAY[ageBucket(ageMs)];
          return (
            <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
              <span title={bucket.label} style={{ width: 9, height: 9, borderRadius: '50%', background: bucket.color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{labelFor(e)}</div>
                <div style={{ fontSize: 10, color: C.textMuted }}>{e.item_count} item{e.item_count === 1 ? '' : 's'} · {formatAgeShort(ageMs)} old · {bucket.label}</div>
              </div>
              <button disabled={busy} onClick={() => onRefreshScope(e)} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, cursor: busy ? 'default' : 'pointer' }}>Refresh</button>
              {e.pinned && <button disabled={busy} onClick={() => onRelease(e.key)} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.amberBorder}`, background: C.amberLight, color: C.amber, cursor: busy ? 'default' : 'pointer' }}>Release</button>}
            </div>
          );
        })}

        {/* Take-offline picker */}
        <div style={{ marginTop: 16, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: C.textMuted }}>TAKE A DATE RANGE OFFLINE</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, color: C.textMuted }}>From<br /><input type="date" value={pickFrom} onChange={(ev) => setPickFrom(ev.target.value)} style={{ fontSize: 12, padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F }} /></label>
          <label style={{ fontSize: 11, color: C.textMuted }}>To<br /><input type="date" value={pickTo} onChange={(ev) => setPickTo(ev.target.value)} style={{ fontSize: 12, padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F }} /></label>
          <button disabled={busy} onClick={onPin} style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Working…' : 'Pin offline'}</button>
        </div>
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6 }}>
          Pinned ranges stay current with each connection until you release them.{estimate && ` Device storage: ${fmtMB(estimate.usage)} / ${fmtMB(estimate.quota)} MB used.`}
        </div>
        {msg && <div style={{ marginTop: 10, fontSize: 11, color: C.text, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px' }}>{msg}</div>}
      </div>
    </ModalOverlay>
  );
}
